import MessageFormatOptionState from '../store/schema/MessageFormatOptionState';
import store from '../store/store';
import { getFallbackValueIfNull } from 'owa-options-core';
import {
    Dropdown,
    DropdownMenuItemType,
    IDropdownOption,
} from 'office-ui-fabric-react/lib/Dropdown';
import UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import { getUserConfiguration } from 'owa-session-store';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import { reactive } from 'satcheljs/lib/legacy/react';
import {
    FONT_NAME_LIST,
    FONT_SIZES,
    getFontSizeInPoints,
    getFontSizeFromSizeOrIndex,
} from 'owa-format-options';
import { getComposeFormat } from 'owa-editor/lib/utils/getDefaultComposeFormat';
import FormatButton from './FormatButton';
import ColorPickerButton from './ColorPickerButton';
import FontFlags from 'owa-service/lib/contract/FontFlags';
import selectFontFamilyOption from '../actions/selectFontFamilyOption';
import selectFontSizeOption from '../actions/selectFontSizeOption';
import selectFormattingOption from '../actions/selectFormattingOption';
import selectTextColorOption from '../actions/selectTextColorOption';
import { OptionDescription } from 'owa-options-controls';

const styles = require('./MessageFormatFull.scss');

export interface ComposeStylesProps {
    optionStore?: MessageFormatOptionState;
    userOptions?: UserOptionsType;
}

@reactive({
    optionStore: () => store,
    userOptions: () => getUserConfiguration().UserOptions,
})
export default class ComposeStyles extends React.Component<ComposeStylesProps, {}> {
    private fontNameItems: IDropdownOption[];
    private fontSizeItems: IDropdownOption[];

    constructor(props: ComposeStylesProps) {
        super(props);

        let dividerIndex = 0;
        this.fontNameItems = FONT_NAME_LIST.map(font => {
            if (font.name === '-') {
                return {
                    key: `divider_${dividerIndex++}`,
                    itemType: DropdownMenuItemType.Divider,
                    text: '-',
                };
            } else {
                return {
                    key: font.name,
                    text: font.name,
                };
            }
        });

        this.fontSizeItems = FONT_SIZES.map((size, index) => ({
            key: getFontSizeFromSizeOrIndex(size), // index from UserOptions is 1-based
            text: getFontSizeInPoints(size),
        }));
    }

    render() {
        let { optionStore, userOptions } = this.props;

        let defaultFontFamily = getFallbackValueIfNull(
            optionStore.fontFamily,
            userOptions.ComposeFontName
        );
        let defaultFontSize = getFallbackValueIfNull(
            getFontSizeFromSizeOrIndex(optionStore.fontSize),
            getFontSizeFromSizeOrIndex(userOptions.ComposeFontSize)
        );
        let isBold = getFallbackValueIfNull<boolean>(
            optionStore.isFontBold,
            (userOptions.ComposeFontFlags & FontFlags.Bold) === FontFlags.Bold
        );
        let isItalic = getFallbackValueIfNull<boolean>(
            optionStore.isFontItalic,
            (userOptions.ComposeFontFlags & FontFlags.Italic) === FontFlags.Italic
        );
        let isUnderline = getFallbackValueIfNull<boolean>(
            optionStore.isFontUnderline,
            (userOptions.ComposeFontFlags & FontFlags.Underline) === FontFlags.Underline
        );

        let textStyles = this.getFormatStyles(
            defaultFontFamily,
            defaultFontSize,
            getFallbackValueIfNull(optionStore.fontColor, userOptions.ComposeFontColor),
            isBold,
            isItalic,
            isUnderline
        );

        return (
            <div className={styles.textFormat}>
                <div className={styles.formatBar}>
                    <Dropdown
                        selectedKey={defaultFontFamily}
                        options={this.fontNameItems}
                        className={styles.fontDropDown}
                        onChanged={this.onFontFamilyChanged}
                    />
                    <Dropdown
                        selectedKey={defaultFontSize}
                        options={this.fontSizeItems}
                        className={styles.sizeDropDown}
                        onChanged={this.onFontSizeChanged}
                    />
                    <FormatButton type="bold" isSelected={isBold} onClick={this.onBoldClicked} />
                    <FormatButton
                        type="italic"
                        isSelected={isItalic}
                        onClick={this.onItalicClicked}
                    />
                    <FormatButton
                        type="underline"
                        isSelected={isUnderline}
                        onClick={this.onUnderlineClicked}
                    />
                    <ColorPickerButton onSelectColor={selectTextColorOption} />
                </div>
                <div className={styles.sampleText} style={textStyles}>
                    <OptionDescription>{strings.options_format_sampleText_1}</OptionDescription>
                    <OptionDescription>{strings.options_format_sampleText_2}</OptionDescription>
                </div>
            </div>
        );
    }

    private onFontFamilyChanged(option: IDropdownOption, index?: number) {
        selectFontFamilyOption(option.key as string);
    }

    private onFontSizeChanged(option: IDropdownOption, index?: number) {
        selectFontSizeOption(option.key as number);
    }

    private onBoldClicked(isSelected: boolean) {
        selectFormattingOption('isFontBold', isSelected);
    }

    private onItalicClicked(isSelected: boolean) {
        selectFormattingOption('isFontItalic', isSelected);
    }

    private onUnderlineClicked(isSelected: boolean) {
        selectFormattingOption('isFontUnderline', isSelected);
    }

    private getFormatStyles(
        fontFamily: string,
        fontSize: number,
        fontColor: string,
        isBold: boolean,
        isItalic: boolean,
        isUnderline: boolean
    ): React.CSSProperties {
        let composeFormat = getComposeFormat(fontFamily, fontSize, fontColor, 0);

        return {
            fontFamily: composeFormat.fontFamily,
            fontSize: composeFormat.fontSize,
            color: composeFormat.textColor,
            fontWeight: isBold ? 'bold' : undefined,
            fontStyle: isItalic ? 'italic' : undefined,
            textDecoration: isUnderline ? 'underline' : undefined,
        };
    }
}
